import { useState } from "react";
import { Check, Star } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { TitleBand } from "@/components/site/TitleBand";
import DOMPurify from "dompurify";
import { SiteShell, useSite } from "@/components/site/SiteShell";
import { SITE_IMG, STEPS, WHY } from "@/lib/siteImages";
import { CtaBanner, Eyebrow, PageHero, Testimonials } from "@/components/site/Blocks";
import { ArrowLink, CountUp, Marquee, Rule } from "@/components/site/Motion";
import { siteImg, usePageHead } from "@/lib/siteContent";
import { useReveal } from "@/lib/useReveal";

const FALLBACK = Object.values(SITE_IMG).filter(
  (v) => typeof v === "string" && v.startsWith("http"),
);

const RICH = {
  ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "h1", "h2", "h3", "ul", "ol", "li", "a", "blockquote", "span", "div"],
  ALLOWED_ATTR: ["href", "target", "rel", "style"],
};
const clean = (html) => DOMPurify.sanitize(html || "", RICH);

const DEFAULT_MAKE = [
  {
    title: "Kitchen islands",
    body: "Vein-matched waterfall ends cut from a single slab, so the pattern runs unbroken over the edge.",
  },
  {
    title: "Bathrooms & vanities",
    body: "Marble and quartz vanities — templated, sealed and set by our own crew.",
  },
  {
    title: "Outdoor kitchens",
    body: "UV-safe porcelain and granite built to live outside without fading or staining.",
  },
  {
    title: "Fireplace surrounds",
    body: "Full-height feature walls with book-matched stone for a single, calm sweep.",
  },
];

const Title = ({ micro, title, testId, className = "" }) => (
  <TitleBand eyebrow={micro} heading={title} className={className} headingTestId={testId} />
);

const CropCorners = () => (
  <>
    <span aria-hidden className="pointer-events-none absolute -left-[3px] -top-[3px] h-2.5 w-2.5 border-l border-t border-[color:var(--s-ink)]/25" />
    <span aria-hidden className="pointer-events-none absolute -right-[3px] -top-[3px] h-2.5 w-2.5 border-r border-t border-[color:var(--s-ink)]/25" />
    <span aria-hidden className="pointer-events-none absolute -bottom-[3px] -left-[3px] h-2.5 w-2.5 border-b border-l border-[color:var(--s-ink)]/25" />
    <span aria-hidden className="pointer-events-none absolute -bottom-[3px] -right-[3px] h-2.5 w-2.5 border-b border-r border-[color:var(--s-ink)]/25" />
  </>
);

// Nordic "About — Our Principle / Our Process / Our Commitment" tabbed block:
// tab row (active = solid ink), then image left + heading, paragraph, 2-col
// check list and a bordered callout card on the right.
const ProcessTabs = ({ tabs }) => {
  const [active, setActive] = useState(0);
  const cur = tabs[active] || tabs[0];
  return (
    <section className="bg-[color:var(--s-surface)] py-24 sm:py-32" data-testid="about-process">
      <div className="s-container">
        <div
          className="s-reveal relative grid grid-cols-3 border border-[color:var(--s-line)]"
          data-testid="about-process-tabs"
        >
          <CropCorners />
          {tabs.map((tb, i) => (
            <button
              key={tb.key}
              type="button"
              onClick={() => setActive(i)}
              aria-pressed={i === active}
              data-testid={`about-process-tab-${tb.key}`}
              className={`border-r border-[color:var(--s-line)] px-3 py-5 text-center text-[11px] font-semibold uppercase tracking-[0.12em] transition-colors duration-300 last:border-r-0 sm:py-6 sm:text-sm ${
                i === active
                  ? "bg-[color:var(--s-ink)] text-white"
                  : "text-[color:var(--s-mute)] hover:text-[color:var(--s-ink)]"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        <div className="mt-8 grid overflow-hidden lg:grid-cols-2">
          <div className="s-fig relative min-h-[300px] overflow-hidden sm:min-h-[420px] lg:min-h-[560px]">
            <img
              key={cur.image}
              src={cur.image}
              alt={cur.heading}
              loading="lazy"
              className="s-fade absolute inset-0 h-full w-full object-cover"
              data-testid="about-process-image"
            />
          </div>
          <div className="flex flex-col justify-center bg-[color:var(--s-cream)] p-8 sm:p-12 lg:p-14">
            <h3 className="s-hx text-3xl leading-[1.1] sm:text-4xl" data-testid="about-process-heading">
              {cur.heading}
            </h3>
            <p
              className="mt-6 max-w-xl text-base leading-relaxed text-[color:var(--s-mute)]"
              data-testid="about-process-body"
            >
              {cur.body}
            </p>
            <ul className="mt-9 grid gap-x-8 gap-y-4 sm:grid-cols-2" data-testid="about-process-points">
              {cur.points.map((p) => (
                <li key={p} className="flex items-start gap-3 text-[15px] font-medium leading-snug">
                  <Check size={18} weight="bold" className="mt-0.5 shrink-0 text-[color:var(--s-gold)]" />
                  {p}
                </li>
              ))}
            </ul>
            {cur.callout && (
              <div
                className="mt-10 flex items-center gap-5 border border-[color:var(--s-line)] bg-[color:var(--s-surface)] p-6"
                data-testid="about-process-callout"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[color:var(--s-ink)] text-white">
                  <Check size={20} weight="bold" />
                </span>
                <p className="text-sm leading-relaxed text-[color:var(--s-mute)]">{cur.callout}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default function SiteAbout() {
  const site = useSite();
  const shop = site?.shop || {};
  const stats = site?.stats || {};
  const cfg = site?.config || {};
  usePageHead({
    title: "About Brioni Granite",
    description:
      "Family-run St. Louis stone yard and fabricator. Walk the racks, choose your exact slab, and we template, cut and install it.",
    suffix: shop?.name,
  });
  const about = cfg.about || {};
  const founder = cfg.founder_quote || {};
  const rating = cfg.stats?.rating || "4.8";
  const reviews = cfg.stats?.reviews || "50";
  const why = cfg.why?.items?.length ? cfg.why.items : WHY;
  const steps = cfg.workflow?.items?.length ? cfg.workflow.items : STEPS;
  useReveal([site]);

  const makeItems = (cfg.projects?.items?.length ? cfg.projects.items : DEFAULT_MAKE)
    .slice(0, 4)
    .map((p, i) => ({
      title: p.title,
      body: p.body || p.category || "",
      image: siteImg(p.image, FALLBACK[i % FALLBACK.length]),
    }));

  const stat1v = about.stat1_value || "150+";
  const stat1l = about.stat1_label || "Countertops installed";
  const stat2v = about.stat2_value || `${rating}★`;
  const stat2l = about.stat2_label || `Across ${reviews} reviews`;
  const expYears = about.exp_years || "25+";
  const expLabel = about.exp_years_label || "Years crafting stone in St. Louis";
  const expNote1 = about.exp_note1 || "Customized design solutions";
  const expNote2 = about.exp_note2 || "Premium materials & finishes";
  const marquee = (
    about.marquee ||
    "Vein-matched seams · Cut in our own shop · Installed by our crew · Container-direct slabs"
  )
    .split("·")
    .map((s) => s.trim())
    .filter(Boolean);

  const statCells = [
    { n: stats.slabs ?? 0, l: "Slabs in stock" },
    { n: stats.colors ?? 0, l: "Colors in the racks" },
    { n: rating, l: `Across ${reviews} reviews`, text: true },
    { n: 1, l: "Shop, start to finish" },
  ];

  const hoursLines = shop.hours_lines?.length
    ? shop.hours_lines
    : cfg.contact?.hours?.filter(Boolean)?.length
      ? cfg.contact.hours.filter(Boolean)
      : [`Mon–Fri ${shop.hours_start || "08:00"}–${shop.hours_end || "17:00"}`];

  const story =
    about.body2 ||
    "Homeowners are welcome to walk in and browse. Builders, kitchen studios and designers get slab holds, shop drawings and schedules they can plan around.";

  const processTabs = [
    {
      key: "principle",
      label: "Our Principle",
      heading: cfg.why?.heading || "Built on doing it ourselves.",
      body:
        cfg.why?.sub ||
        "The slab you pick is the slab we cut and set. Nothing is sub-contracted — from the racks to your kitchen you deal with the same people who actually make your countertops.",
      points: why.map((w) => w.title).slice(0, 6),
      callout:
        founder.quote ||
        "Ensuring the slab you choose is the slab we install is a top priority for our crew on every job.",
      image: siteImg(about.image, SITE_IMG.warehouse),
    },
    {
      key: "process",
      label: "Our Process",
      heading: cfg.workflow?.heading || "One shop, start to finish.",
      body:
        cfg.workflow?.sub ||
        "From the first walk through the racks to the final install, every step happens under one roof with one crew — no middle shop, no surprises.",
      points: steps.map((s) => s.title).slice(0, 6),
      callout:
        "Consult, pick your slab, template, fabricate and install — usually inside a couple of weeks.",
      image: siteImg(founder.image2 || about.image, SITE_IMG.fabrication),
    },
    {
      key: "commitment",
      label: "Our Commitment",
      heading: about.commitment_heading || "Your project, our commitment.",
      body:
        about.commitment_body ||
        "We stand behind every seam and edge — honest stone work, done in-house and backed by a shop that still answers the phone afterwards.",
      points:
        marquee.length >= 4
          ? marquee
          : ["Vein-matched seams", "Cut in our own shop", "Installed by our crew", "Container-direct slabs"],
      callout: `${rating}★ across ${reviews} Google reviews — the reputation we protect on every countertop.`,
      image: siteImg(makeItems[0]?.image, SITE_IMG.polishing),
    },
  ];

  return (
    <SiteShell site={site} testId="site-about" overHero>
      <PageHero
        testId="about-hero"
        titleTestId="about-hero-heading"
        bodyTestId="about-hero-body"
        image={siteImg(about.image, SITE_IMG.warehouse)}
        eyebrow={about.eyebrow || "About us"}
        title={about.heading || "A stone warehouse you can walk into"}
        bodyHtml={
          clean(about.body) ||
          "Brioni Granite is a premier supplier and fabricator of natural and engineered stone in St. Louis. We buy slabs by the container, keep them standing in our own racks and cut them in our own shop — so you deal with the people who actually make your countertops."
        }
      >
        <div className="mt-10 flex flex-wrap gap-10">
          <div data-testid="about-stat-1">
            <div className="s-num text-4xl text-[color:var(--s-gold-bright)] sm:text-5xl">{stat1v}</div>
            <div className="s-micro mt-2 text-white/55">{stat1l}</div>
          </div>
          <div data-testid="about-stat-2">
            <div className="s-num text-4xl text-[color:var(--s-gold-bright)] sm:text-5xl">{stat2v}</div>
            <div className="s-micro mt-2 text-white/55">{stat2l}</div>
          </div>
        </div>
      </PageHero>

      {/* Experience strip — light, hairline grid */}
      <section className="border-b border-[color:var(--s-line)] bg-[color:var(--s-surface)]" data-testid="about-experience-strip">
        <div className="s-reveal s-container grid sm:grid-cols-3 lg:divide-x lg:divide-[color:var(--s-line)]">
          <div className="py-12 lg:pr-12">
            <div className="s-num text-6xl sm:text-7xl" data-testid="about-exp-years">
              {expYears}
            </div>
            <div className="s-micro mt-4 text-[color:var(--s-gold)]">{expLabel}</div>
          </div>
          {[expNote1, expNote2].map((note, i) => (
            <div key={note} className="flex flex-col justify-center py-12 lg:px-12" data-testid={`about-exp-value-${i + 1}`}>
              <span className="s-num text-sm text-[color:var(--s-gold)]">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="s-hx mt-4 text-2xl sm:text-3xl">
                <strong>{note}</strong>
              </h3>
            </div>
          ))}
        </div>
      </section>

      {/* Our story — Maiko introduction pattern */}
      <section className="bg-[color:var(--s-bg)] pt-24 sm:pt-32" data-testid="about-warehouse-block">
        <div className="s-container">
          <Title micro={founder.micro || "Our story"} title={about.story_heading || "Our story."} testId="about-story-heading" />
          <Rule className="mt-12" />
          <div className="grid gap-12 py-14 lg:grid-cols-[1fr_1fr] lg:gap-20 lg:py-20">
            <div className="s-reveal">
              <p className="text-2xl font-light leading-snug sm:text-3xl">
                {founder.quote ||
                  "Brioni Granite is committed to giving you the best stone in the region — the slab you pick is the slab we cut and set."}
              </p>
              <div className="mt-10 flex items-center gap-4">
                <img
                  src={siteImg(founder.image, SITE_IMG.fabrication)}
                  alt={founder.author || "Our team"}
                  className="h-16 w-16 rounded-full object-cover grayscale"
                  data-testid="about-warehouse-image"
                />
                <div>
                  <div className="text-sm font-semibold">{founder.author || "Brioni Granite"}</div>
                  {founder.role && <div className="s-micro mt-1 text-[color:var(--s-mute)]">{founder.role}</div>}
                </div>
              </div>
            </div>
            <div className="s-reveal" data-delay="1">
              <h3 className="s-hx text-3xl sm:text-4xl">
                <strong>{founder.heading || "Family-owned & operated, St. Louis"}</strong>
              </h3>
              {story && (
                <div
                  className="s-prose mt-6 text-base leading-relaxed text-[color:var(--s-mute)]"
                  data-testid="about-story-body"
                  dangerouslySetInnerHTML={{ __html: clean(story) }}
                />
              )}
              {founder.body && (
                <p className="mt-5 text-base leading-relaxed text-[color:var(--s-mute)]">{founder.body}</p>
              )}
              <ArrowLink to="/contact" className="mt-10" testId="about-quote">
                Plan your project
              </ArrowLink>
            </div>
          </div>
        </div>
        <Marquee speed={55} className="s-reveal" testId="about-image-marquee">
          {[siteImg(founder.image2 || about.image, SITE_IMG.warehouse), ...FALLBACK.slice(0, 7)].map((src, i) => (
            <img
              key={i}
              src={src}
              alt=""
              loading="lazy"
              className="mr-4 h-56 w-[19rem] shrink-0  object-cover grayscale-[35%] transition-[filter] duration-500 hover:grayscale-0 sm:h-72 sm:w-[24rem]"
            />
          ))}
        </Marquee>
      </section>

      {/* Live counters */}
      <section className="bg-[color:var(--s-bg)] py-20">
        <dl className="s-reveal s-container grid grid-cols-2 gap-px overflow-hidden  bg-[color:var(--s-line)] lg:grid-cols-4" data-testid="about-stats">
          {statCells.map((s, i) => (
            <div key={s.l} className="bg-[color:var(--s-surface)] px-6 py-10 sm:px-8 sm:py-12" data-testid="about-stat-cell">
              <div className="s-num text-sm text-[color:var(--s-gold)]">{String(i + 1).padStart(2, "0")}.</div>
              <dt className="s-num mt-4 text-5xl sm:text-6xl">{s.text ? s.n : <CountUp value={s.n} />}</dt>
              <dd className="s-micro mt-3 text-[color:var(--s-mute)]">{s.l}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* What we make — stacked rows */}
      <section className="bg-[color:var(--s-surface)] py-24 sm:py-32" data-testid="about-capabilities">
        <div className="s-container">
          <Title
            micro={about.styles_eyebrow || "Our work"}
            title={about.styles_heading || "Every kind of home."}
            testId="about-capabilities-heading"
          />
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:gap-10" data-testid="about-capabilities-grid">
            {makeItems.map((m, i) => (
              <article
                key={`${m.title}-${i}`}
                className="s-reveal group"
                data-testid={`about-capability-${i}`}
                data-delay={String(i % 2)}
              >
                <Link
                  to="/gallery"
                  aria-label={`${m.title} — see examples`}
                  className="s-fig s-soft block aspect-[4/3] overflow-hidden"
                >
                  <img
                    src={m.image}
                    alt={m.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(.2,.7,.2,1)] group-hover:scale-[1.04]"
                  />
                </Link>
                <div className="mt-6 flex items-start gap-4">
                  <span className="s-num shrink-0 pt-1 text-sm text-[color:var(--s-gold)]">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="s-hx text-2xl sm:text-3xl">
                      <strong>{m.title}</strong>
                    </h3>
                    {m.body && <p className="mt-3 text-base leading-relaxed text-[color:var(--s-mute)]">{m.body}</p>}
                    <ArrowLink to="/gallery" className="mt-6">
                      See examples
                    </ArrowLink>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Marquee band */}
      <section className="border-y border-[color:var(--s-line)] bg-[color:var(--s-cream)] py-10" data-testid="about-marquee-band">
        <Marquee speed={40} testId="about-marquee-track">
          {marquee.map((phrase, i) => (
            <span key={i} className="s-hx mx-8 flex items-center gap-8 whitespace-nowrap text-3xl sm:text-5xl">
              {phrase}
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-[color:var(--s-gold-bright)]" />
            </span>
          ))}
        </Marquee>
      </section>

      {/* Why us */}
      <section className="bg-[color:var(--s-surface)] py-24 sm:py-32" data-testid="about-why-us">
        <div className="s-container grid gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-20">
          <div className="s-reveal">
            <TitleBand eyebrow="Why choose us" heading={cfg.why?.heading || "Why choose us."} align="left" headingTestId="about-why-heading" />
            {cfg.why?.sub && <p className="mt-8 max-w-md text-base leading-relaxed text-[color:var(--s-mute)]">{cfg.why.sub}</p>}
          </div>
          <ol>
            {why.map((w, i) => (
              <li key={w.title} className="s-reveal grid gap-4 border-t border-[color:var(--s-line)] py-8 sm:grid-cols-[4rem_1fr]" data-testid="about-why-card" data-delay={String(i % 3)}>
                <span className="s-num text-sm text-[color:var(--s-gold)]">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="text-xl font-semibold">{w.title}</h3>
                  <p className="mt-3 max-w-lg text-base leading-relaxed text-[color:var(--s-mute)]">{w.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Process — Nordic tabbed principle / process / commitment */}
      <ProcessTabs tabs={processTabs} />

      <Testimonials data={cfg.testimonials} testId="about-testimonials" prefix="about-review" />

      {/* Contact band */}
      <section className="bg-[color:var(--s-surface)] py-20 sm:py-24" data-testid="about-contact-band">
        <div className="s-reveal s-container grid gap-12 sm:grid-cols-3 lg:divide-x lg:divide-[color:var(--s-line)]">
          {shop.address && (
            <a
              href={`https://maps.google.com/?q=${encodeURIComponent(shop.address)}`}
              target="_blank"
              rel="noreferrer"
              data-testid="about-address"
              className="group lg:pr-12"
            >
              <Eyebrow>Warehouse &amp; showroom</Eyebrow>
              <div className="mt-5 whitespace-pre-line text-lg font-semibold leading-snug transition-colors duration-300 group-hover:text-[color:var(--s-gold)]">{shop.address}</div>
              <span className="s-arrow-link mt-6 !text-[10px]">
                <span>Directions</span>
                <span className="s-arrow" />
              </span>
            </a>
          )}
          <div className="lg:px-12">
            <Eyebrow>{cfg.contact?.hours_label || "Hours"}</Eyebrow>
            <div className="mt-5 space-y-2 text-base" data-testid="about-hours">
              {hoursLines.map((line) => (
                <span key={line} className="block border-b border-[color:var(--s-line)] pb-2">
                  {line}
                </span>
              ))}
            </div>
          </div>
          <div className="lg:pl-12" data-testid="about-reputation">
            <Eyebrow>Reputation</Eyebrow>
            <div className="mt-5 flex gap-1 text-[color:var(--s-gold-bright)]">
              {Array.from({ length: 5 }).map((_, k) => (
                <Star key={k} size={14} weight="fill" />
              ))}
            </div>
            <div className="mt-4 text-lg font-semibold">
              {rating} out of 5 across {reviews} Google reviews
            </div>
            {shop.phone && (
              <ArrowLink href={`tel:${shop.phone_link || shop.phone}`} className="mt-6 !text-[10px]">
                {shop.phone}
              </ArrowLink>
            )}
          </div>
        </div>
      </section>

      <CtaBanner cfg={cfg.cta} shop={shop} image={siteImg(cfg.cta?.image || cfg.why?.image, SITE_IMG.kitchen2)} testId="about-cta" />
    </SiteShell>
  );
}
